/**
 * ReasonModal — AWLP Design System
 * Generic mini modal that collects a required justification before a
 * state-changing action proceeds. Generalized from SkipTaskModal — the
 * reason is required (min length, trimmed), resets on open, and disables
 * while a submission is in flight. Consumers supply the copy (title,
 * prompt, confirm label) and, optionally, a server-returned error to show
 * inline without closing the modal.
 */

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle } from 'lucide-react';
import { Modal } from '../Modal';
import { Button } from '../Button';

const DEFAULT_MIN_REASON_LENGTH = 5;
const DEFAULT_MAX_REASON_LENGTH = 500;
const DEFAULT_FIELD_ID = 'reason-modal-textarea';

// A plain div, not a <label> — `prompt` is a ReactNode and may contain block
// content (e.g. a <ul> of open tasks), which isn't valid inside a <label>.
// Associated to the textarea via aria-labelledby instead of htmlFor.
const Prompt = styled.div`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.backgrounds.light};
    cursor: not-allowed;
  }
`;

const Helper = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 4px 0 0;
`;

const ErrorText = styled.p`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 4px 0 0;
`;

export interface ReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isSubmitting?: boolean;
  title: string;
  prompt: React.ReactNode;
  confirmLabel: string;
  submittingLabel?: string;
  variant?: 'primary' | 'danger';
  minLength?: number;
  maxLength?: number;
  zIndex?: number;
  fieldId?: string;
  /** Externally-supplied error (e.g. a server rejection) shown inline without closing the modal. */
  error?: string | null;
}

export const ReasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  title,
  prompt,
  confirmLabel,
  submittingLabel = 'Submitting…',
  variant = 'primary',
  minLength = DEFAULT_MIN_REASON_LENGTH,
  maxLength = DEFAULT_MAX_REASON_LENGTH,
  zIndex,
  fieldId = DEFAULT_FIELD_ID,
  // Owned entirely by the caller: cleared only when the caller clears it
  // (e.g. on a fresh attempt), not by this modal's internal reset-on-open.
  error: externalError = null,
}: ReasonModalProps) => {
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset the field each time the modal opens for a fresh action.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setValidationError('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (trimmed.length < minLength) {
      setValidationError(`Justification must be at least ${minLength} characters.`);
      return;
    }
    onConfirm(trimmed);
  };

  const displayedError = validationError || externalError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      zIndex={zIndex}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant={variant} size="sm" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? submittingLabel : confirmLabel}
          </Button>
        </>
      }
    >
      <Prompt id={`${fieldId}-label`}>{prompt}</Prompt>
      <Textarea
        id={fieldId}
        aria-labelledby={`${fieldId}-label`}
        autoFocus
        value={reason}
        maxLength={maxLength}
        disabled={isSubmitting}
        placeholder="Enter a justification — this is recorded in the activity log…"
        onChange={(e) => {
          setReason(e.target.value);
          if (validationError) setValidationError('');
        }}
      />
      {displayedError ? (
        <ErrorText>
          <AlertCircle size={12} /> {displayedError}
        </ErrorText>
      ) : (
        <Helper>Minimum {minLength} characters. This justification is logged.</Helper>
      )}
    </Modal>
  );
};

export default ReasonModal;
